import type { CollectedData } from '../../context/RecommendationContext';

const EMPTY_VALUES = new Set(['unknown', 'not_sure', 'unsure', 'n/a', 'none', '']);

export const isMissingValue = (value: unknown): boolean => {
  if (value == null) return true;
  if (typeof value === 'string') {
    return EMPTY_VALUES.has(value.trim().toLowerCase());
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
};

const toTitleCase = (value: string): string =>
  value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatArrayValue = (value: unknown[]): string => {
  if (value.length === 0) return 'Not provided';

  const items = value
    .map((item) => {
      if (typeof item === 'string' || typeof item === 'number') {
        return formatReportValue(item);
      }
      if (item && typeof item === 'object') {
        const record = item as Record<string, unknown>;
        if ('boiler' in record || 'water' in record) {
          const boiler = record.boiler ? formatReportValue(record.boiler) : null;
          const water = record.water ? formatReportValue(record.water) : null;
          return [boiler, water].filter(Boolean).join(' + ');
        }
        // Handle priority ranking format: { key: string, rank: 1 | 2 | 3 }
        if ('key' in record && 'rank' in record) {
          const rank = record.rank;
          const rankSuffix = rank === 1 ? '1st' : rank === 2 ? '2nd' : rank === 3 ? '3rd' : `${rank}`;
          const priorityLabel = formatReportValue(record.key);
          return `${priorityLabel} (${rankSuffix})`;
        }
        return Object.values(record)
          .map((entry) => formatReportValue(entry))
          .filter((entry) => entry !== 'Not provided')
          .join(', ');
      }
      return 'Not provided';
    })
    .filter((entry) => entry && entry !== 'Not provided');

  return items.length > 0 ? items.join(', ') : 'Not provided';
};

export const formatReportValue = (value: unknown): string => {
  if (isMissingValue(value)) {
    return 'Not provided';
  }

  if (Array.isArray(value)) {
    return formatArrayValue(value);
  }

  if (typeof value === 'number') {
    return value.toString();
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    const lower = trimmed.toLowerCase();

    if (lower === 'yes') return 'Yes';
    if (lower === 'no') return 'No';
    if (lower === 'not_sure' || lower === 'unsure') return 'Not sure';

    return toTitleCase(trimmed);
  }

  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map((entry) => formatReportValue(entry))
      .filter((entry) => entry !== 'Not provided')
      .join(', ') || 'Not provided';
  }

  return 'Not provided';
};

export const getValueFromPath = (data: CollectedData | null | undefined, path: string): unknown => {
  if (!data) return null;

  const segments = path.split('.');
  let current: unknown = data;

  for (const segment of segments) {
    if (current && typeof current === 'object' && segment in (current as Record<string, unknown>)) {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return null;
    }
  }

  return current;
};

export const buildSummaryLine = (...values: Array<unknown>): string => {
  const formatted = values
    .map((value) => formatReportValue(value))
    .filter((value) => value !== 'Not provided');

  return formatted.length ? formatted.join(' • ') : 'Not provided';
};
